import { Link, useParams } from "react-router-dom";
import { Phone, Check } from "lucide-react";
import { CITIES, SERVICES, PHONE, PHONE_HREF, BOOKING_URL } from "@/data/site";
import { Seo } from "@/components/Seo";
import { LeadForm } from "@/components/LeadForm";
import NotFound from "@/pages/NotFound";

const CityHero = ({ city }) => (
  <section className="bg-brand-ink text-white">
    <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-ice mb-3">Serving {city.name}, Florida</p>
        <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-black max-w-3xl">
          AC Repair &amp; Maintenance in {city.name}
        </h1>
        <p className="text-base sm:text-lg text-zinc-300 mt-6 max-w-2xl leading-relaxed">{city.blurb}</p>
        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="city-book-btn"
            className="bg-brand-ice hover:bg-[#7FA8F0] text-brand-ink font-bold px-8 py-4 rounded-sm text-center transition-colors duration-200">
            Book Service in {city.name}
          </a>
          <a href={PHONE_HREF} data-testid="city-call-btn"
            className="border-2 border-white text-white hover:bg-white hover:text-brand-ink font-bold px-8 py-4 rounded-sm text-center flex items-center justify-center gap-2 transition-colors duration-200">
            <Phone className="w-5 h-5" /> {PHONE}
          </a>
        </div>
      </div>
      <div data-testid="city-lead-form-wrap">
        <LeadForm heading={`Get AC Help in ${city.name}`} subtext="Tell us what's going on — we'll call you back fast, usually within the hour." />
      </div>
    </div>
  </section>
);

const CityServices = ({ city }) => (
  <section className="bg-white">
    <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24">
      <h2 className="text-2xl sm:text-3xl tracking-tight font-black text-brand-ink mb-10">Flat-rate AC services in {city.name}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SERVICES.map((s) => (
          <div key={s.id} data-testid={`city-service-${s.id}`} className="flex items-start justify-between gap-4 border border-zinc-200 p-6 rounded-sm hover:border-brand transition-colors duration-200">
            <div>
              <h3 className="font-heading font-bold text-lg text-brand-ink">{s.name}</h3>
              <p className="text-sm text-zinc-600 mt-1 flex items-start gap-1.5"><Check className="w-4 h-4 text-brand shrink-0 mt-0.5" />{s.tagline}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-heading font-black text-2xl text-brand">{s.price}</p>
              <p className="text-xs text-zinc-500">{s.unit}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const NearbyCities = ({ others }) => (
  <section className="bg-brand-subtle">
    <div className="max-w-7xl mx-auto px-4 py-16">
      <h2 className="text-xl sm:text-2xl tracking-tight font-black text-brand-ink mb-6">Also serving nearby</h2>
      <div className="flex flex-wrap gap-3">
        {others.map((c) => (
          <Link key={c.slug} to={`/areas/${c.slug}`} data-testid={`city-nearby-${c.slug}`}
            className="bg-white border border-zinc-200 px-5 py-3 rounded-sm text-sm font-medium text-brand-ink hover:border-brand hover:text-brand transition-colors duration-200">
            {c.name}
          </Link>
        ))}
        <Link to="/service-areas" data-testid="city-all-areas-link"
          className="bg-brand text-white px-5 py-3 rounded-sm text-sm font-bold hover:bg-brand-dark transition-colors duration-200">
          All Service Areas
        </Link>
      </div>
    </div>
  </section>
);

export default function CityPage() {
  const { slug } = useParams();
  const city = CITIES.find((c) => c.slug === slug);

  if (!city) return <NotFound />;
  const others = CITIES.filter((c) => c.slug !== slug).slice(0, 6);

  return (
    <div data-testid={`city-page-${slug}`}>
      <Seo
        title={`AC Repair & Maintenance in ${city.name}, FL | Central Air Depot`}
        description={`AC repair, maintenance and installs in ${city.name}, FL. Same-day service, flat upfront pricing and licensed, insured contractors. Call 786-512-1929.`}
      />
      <CityHero city={city} />
      <CityServices city={city} />
      <NearbyCities others={others} />
    </div>
  );
}
